/**
 * dateFormatter — pure, presentational date/time formatting for the design system.
 *
 * Lives in `ports/primary/ui/formatters/` (a sibling of `ui/types/`), outside `design-system/`, so DS components
 * stay business-agnostic while importing only pure, Storybook-safe helpers. `moment` (the app-wide
 * standard, French locale) is the single dependency; no `@data` / store / service imports.
 */
import moment from "moment"
import "moment/locale/fr"

/** True when the ISO date falls on the calendar day of `now` (en millisecondes) — aujourd'hui par défaut. */
export const isToday = (iso: string, now: number = Date.now()): boolean => moment(iso).isSame(moment(now), "day")

/** True when the ISO date falls on tomorrow. */
const isTomorrow = (iso: string): boolean => moment(iso).isSame(moment().add(1, "day"), "day")

/**
 * Day label for a section header or a card.
 * - `relative` (default `true`): today → "Aujourd'hui", tomorrow → "Demain".
 * - otherwise the full French date, first-letter capitalized, e.g. "Lundi 20 juillet".
 * - `withYear` (défaut `false`) : l'année, seulement quand ce n'est pas l'année en cours
 *   (« Jeudi 24 septembre » cette année, « Mardi 16 décembre 2025 » avant) — une liste qui remonte
 *   le temps, comme les rapports passés, ne redit pas l'année à chaque ligne.
 */
export function formatDayLabel(iso: string, relative = true, withYear = false): string {
    if (relative && isToday(iso)) return "Aujourd'hui"
    if (relative && isTomorrow(iso)) return "Demain"
    const date = moment(iso).locale("fr")
    const s = date.format(withYear && !date.isSame(moment(), "year") ? "dddd D MMMM YYYY" : "dddd D MMMM")
    return s.charAt(0).toUpperCase() + s.slice(1)
}

/**
 * Day header with the full date, capitalized — e.g. "Lundi • 06/07/2026" (revision-program sections).
 * Mirrors the legacy `DaySectionComponent` header format.
 */
export function formatDayDotDate(iso: string): string {
    const day = moment(iso).locale("fr").format("dddd")
    const capitalized = day.charAt(0).toUpperCase() + day.slice(1)
    return `${capitalized} • ${moment(iso).format("DD/MM/YYYY")}`
}

/** Start time only, e.g. "10:30" (locale-independent). */
export const formatTime = (iso: string): string => moment(iso).format("HH:mm")

/**
 * L'heure telle qu'on l'écrit à un parent : « 18h00 ». Le deux-points est une notation d'horaire
 * de service ; dans l'espace parent la marque écrit l'heure comme on la dit. `compact` (défaut
 * `false`) tait des minutes nulles — « 19h », « 19h30 » — la forme d'un créneau de la semaine.
 */
export const formatHour = (iso: string, compact = false): string => {
    const date = moment(iso)
    return compact && date.minutes() === 0 ? date.format("H[h]") : date.format(compact ? "H[h]mm" : "HH[h]mm")
}

/**
 * Whole days remaining until an ISO date, from start-of-day, floored at 0 (never negative) — drives
 * the "J-n" countdown. Mirrors the legacy backend `getDaysUntilAttribute`.
 */
export function daysUntil(iso: string): number {
    return Math.max(0, moment(iso).startOf("day").diff(moment().startOf("day"), "days"))
}

/**
 * Le compte à rebours d'un jour (`daysUntil`) : « J-3 », « Aujourd'hui » le jour même. `inSentence` : « aujourd'hui »,
 * après un mot (« Contrôle aujourd'hui »).
 */
export function formatDaysUntil(days: number, inSentence = false): string {
    if (days > 0) return `J-${days}`
    return inSentence ? "aujourd'hui" : "Aujourd'hui"
}

/** Minutes → "1h" | "1h30" | "45min" (mirrors legacy `Utils.formatMinutes`). */
export function formatDuration(minutes: number): string {
    const h = Math.floor(minutes / 60)
    const m = minutes % 60
    if (h === 0) return `${m}min`
    return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, "0")}`
}

/** Short assessment date, e.g. "mar. 12 mars" (locale-dependent — French when the app locale is set). */
export function formatShortDate(iso: string): string {
    return moment(iso).format("ddd D MMM")
}

/**
 * Jour + mois, e.g. « 22 sept. » — la date courte d'un contrôle, sur une ligne. `abbreviated`
 * (défaut `true`) à `false` écrit le mois en entier, « 22 septembre », la forme d'une phrase
 * (« Rattrapage à planifier avant le 4 octobre ») ; `"numeric"` l'écrit en chiffres, « 13/06 »,
 * une échéance glissée entre parenthèses.
 */
export function formatDayMonth(iso: string, abbreviated: boolean | "numeric" = true): string {
    if (abbreviated === "numeric") return moment(iso).format("DD/MM")
    return moment(iso)
        .locale("fr")
        .format(abbreviated ? "D MMM" : "D MMMM")
}

/**
 * Une période dans une phrase : « Du 19 au 30 octobre », « Du 26 octobre au 1er novembre » quand
 * elle change de mois, « Le 19 octobre » quand elle tient en un jour. Sans année : la période
 * annoncée est toujours la prochaine.
 */
export function formatDateRange(startIso: string, endIso: string): string {
    const start = moment(startIso).locale("fr")
    const end = moment(endIso).locale("fr")
    if (start.isSame(end, "day")) return `Le ${start.format("Do MMMM")}`
    const from = start.format(start.isSame(end, "month") ? "Do" : "Do MMMM")
    return `Du ${from} au ${end.format("Do MMMM")}`
}

/**
 * Full French day+month+year, e.g. "07 juillet 2026" (legacy cancel-dialog copy).
 * `padded` (default `true`) zero-pads the day ; `false` rend « 7 juillet 2026 », la forme que la
 * résiliation emploie à partir de l'étape de paiement, où la date vit dans une phrase.
 */
export function formatDayMonthYear(iso: string, padded = true): string {
    return moment(iso)
        .locale("fr")
        .format(padded ? "DD MMMM YYYY" : "D MMMM YYYY")
}

/** Le mois d'un calendrier, « Novembre 2026 » ; prend un mois `YYYY-MM` ou un jour. */
export function formatMonthYear(month: string): string {
    const label = moment(month.slice(0, 7), "YYYY-MM").locale("fr").format("MMMM YYYY")
    return label.charAt(0).toUpperCase() + label.slice(1)
}

/**
 * Long French assessment date for the form field, e.g. "lundi 19 juillet 2026"
 * (matches legacy `ManualFormSection` / `moment(date).format("dddd D MMMM YYYY")`).
 */
export function formatLongDate(iso: string): string {
    return moment(iso).locale("fr").format("dddd D MMMM YYYY")
}

/**
 * Jour et mois en chiffres, « 22/10 » ; `withWeekday` les fait précéder du jour abrégé,
 * « mer. 15/10 » — une échéance proche, où le jour de la semaine se lit avant la date.
 */
export function formatNumericDayMonth(iso: string, withWeekday = false): string {
    return moment(iso)
        .locale("fr")
        .format(withWeekday ? "ddd DD/MM" : "DD/MM")
}

/**
 * Une échéance proche, sur une ligne : « mer. 15/10 à 19h » — le jour abrégé, la date en
 * chiffres, l'heure sans minutes nulles.
 */
export function formatShortDayTime(iso: string): string {
    return `${formatNumericDayMonth(iso, true)} à ${formatHour(iso, true)}`
}

/** Format a `Date` as `YYYY-MM-DD` (form / API wire format). */
export function toDateOnly(date: Date): string {
    return moment(date).format("YYYY-MM-DD")
}

/**
 * Day + start time on one line.
 * `relative` (default `false`) switches the day part to "Aujourd'hui" / "Demain"; the parent
 * home keeps the absolute date because a separate "Aujourd'hui" chip already carries that.
 * `form` : `"label"` (défaut) pour une ligne seule — « Jeudi 17 septembre · 18h00 » ;
 * `"sentence"` au milieu d'une phrase — « la séance du jeudi 17 septembre à 18h00 » ;
 * `"complement"` en complément de nom, l'article accordé au jour — « la séance d'aujourd'hui à
 * 18h00 », « de demain à 18h00 », « du jeudi 17 septembre à 18h00 ».
 * `withYear` : celui de `formatDayLabel` (défaut `false`, jamais l'année).
 */
export function formatDayTime(
    iso: string,
    relative = false,
    form: "label" | "sentence" | "complement" = "label",
    withYear = false
): string {
    const day = formatDayLabel(iso, relative, withYear)
    if (form === "complement") {
        const sentence = formatDayTime(iso, relative, "sentence", withYear)
        if (relative && isToday(iso)) return `d'${sentence}`
        return `${relative && isTomorrow(iso) ? "de" : "du"} ${sentence}`
    }
    if (form === "sentence") return `${day} à ${formatHour(iso)}`.toLowerCase()
    return `${day} · ${formatHour(iso)}`
}

/** Whole minutes from now until an ISO datetime — negative once it has started. */
export function minutesUntil(iso: string): number {
    return moment(iso).diff(moment(), "minutes")
}

/** « 2 min 14 s », « 45 s », « 3 min » — une durée courte en minutes et secondes, pas en 134. */
export function formatMinutesSeconds(seconds: number): string {
    const whole = Math.round(seconds)
    const minutes = Math.floor(whole / 60)
    const rest = whole % 60
    if (minutes === 0) return `${rest} s`
    return rest === 0 ? `${minutes} min` : `${minutes} min ${rest} s`
}

/**
 * « 12:05 », « 00:40 », « -02:10 » : un nombre de secondes en minutes et secondes, deux chiffres
 * chacun, signé quand il est négatif. Le chrono d'une activité écrit ainsi le temps qui reste (et
 * le dépassement, sous zéro) ; l'écran de fin, le temps passé.
 */
export function formatCountdown(seconds: number): string {
    const whole = Math.trunc(seconds)
    const sign = whole < 0 ? "-" : ""
    const abs = Math.abs(whole)
    const pad = (n: number) => String(n).padStart(2, "0")
    return `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`
}
